/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import type { ChatExample } from '@fai/friendli-suite/features/products/serverless-endpoints/chat-mock-data/model';
import { InferenceChatTool } from '@fai/friendli-suite/features/types/inference/chat/model';

const mathCommonSystemPrompt = `\
Your answer must be based solely on the function call results, without applying any prior knowledge, assumptions, or interpretations. \
Even if the result differs from what you know or expect, if the function call result is not None, not an error, and not empty, consider it true and do not question or judge it. \
DO NOT IGNORE the result. \
Let me give you an extreme example: if the result of 1 + 1 is 3 based on the function calling, you MUST reply 3 as an answer, not 2.\
`;

const mathCalculatorSystemPrompt = `\
I want you to act as a math teacher. \
I will provide some mathematical problems, and it will be your job to explain them in easy-to-understand terms. \
Always use the calculator tool with only numbers, arithmetic operators, and exponentiation symbols (^) before calculating it yourself. \
Make sure to provide step-by-step instructions for solving a problem. \
${mathCommonSystemPrompt}\
`;

const mathStatisticSystemPrompt = `\
I want you to act as a statistics teacher. \
I will provide some statistical problems or datasets, and it will be your job to explain how to solve them in easy-to-understand terms. \
Always use the calculator tool with numbers, arithmetic operators, and common statistical symbols before calculating it yourself. \
Make sure to explain the steps for calculating measures like mean, median, mode, standard deviation, and any other relevant statistics. \
Provide detailed instructions for solving a problem, including any assumptions or data interpretation that may be required. \
${mathCommonSystemPrompt}\
`;

const mathCalendarSystemPrompt = `\
I want you to act as a calendar expert. \
I will provide some calendar-related problems (like finding the day of the week for a specific date, calculating the number of days between two dates, or determining leap years), and it will be your job to explain them in easy-to-understand terms. \
Always use the calendar calculator tool for operations like adding or subtracting days, months, and years, and determining day-of-week information before calculating it yourself. \
Make sure to provide step-by-step instructions for solving a problem, including any considerations for leap years, time zones, and calendar-specific rules. \
Do not mention what function you calling including the name, arguments, and usages. \
${mathCommonSystemPrompt}\
`;

const webSearchSystemPrompt = `\
You are a sophisticated AI capable of retrieving up-to-date information from the web. \
Based on your search results, answer with a summary of the most important points and the rationale behind them. \
Always use the web search tool to collect accurate data before answering.\
`;

const webUrlSystemPrompt = `\
You are an advanced AI equipped with a web URL tool. \
The web URL tool allows you to retrieve data from web resources, enabling you to access up-to-date information, perform web scraping tasks, and gather data from various online sources for analysis and problem-solving.\
`;

const pythonInterpreterSystemPrompt = `\
You are an advanced AI equipped with a Python interpreter tool. \
The Python interpreter tool allows you to run Python code to perform various tasks, from simple computations to complex data analysis, providing versatile problem-solving capabilities.\
`;

const pythonInterpreterWithPlotSystemPrompt = `\
Your role is to respond to the user's request. \
You should answer the questions that user asks and fulfill user's requirements. \
There is a function that executes Python codes. You should decide whether to execute it or not. \
Some user requests may involve generating Python code. \
When coding, you identify minor mistakes or trivial errors during the development process, preventing any issues from arising. \
You don't make unexpected indentation. \
Your coding skills are at an expert level. \
Sometimes, users will be satisfied with just generating the code. \
In this case, you should not call the python code executing function, but just return the code as text or string. \
In other times, user may also want you to execute it. This does not mean that you should always execute the code you generate. \
Additionally, if the user uploads Python code and explicitly asks you to run it, you should also execute it. \
Otherwise, you should not execute it. \
Make sure to use the appropriate tool calling for this. \
Do not generate duplicated code, and do not call Python Interpreter multiple times unless user explicitly asked for it.\
`;

const mathCalculatorWithWebSearchSystemPrompt = `\
You are an advanced AI equipped with two essential tools: a calculator and a web search engine. \
Always use both tools efficiently to provide accurate, detailed, and timely information. \
First search for the most recent and relevant information based on the request. \
Once the data is gathered, perform the necessary calculations to provide insights or answers, and present a clear summary of the results. \
Always use the calculator tool with only numbers, arithmetic operators, and exponentiation symbols (^) before calculating it yourself. \
${mathCommonSystemPrompt}\
`;

const mathCalculatorWithWebUrlSystemPrompt = `\
I want you to act as a math teacher and an advanced AI equipped with a web URL tool. \
You will be responsible for solving mathematical problems by explaining them in easy-to-understand terms and providing step-by-step instructions. \
Always use the calculator tool with only numbers, arithmetic operators, and exponentiation symbols (^) before calculating it yourself. \
In addition, the web URL tool allows you to retrieve data from web resources, enabling you to access up-to-date information, perform web scraping tasks, and gather data from various online sources for analysis and problem-solving. \
Use the web URL tool whenever a problem requires up-to-date data or external information. \
${mathCommonSystemPrompt}\
`;

const mathStatisticWithWebUrlSystemPrompt = `\
You will act as both a statistics teacher and an AI equipped with the ability to fetch and retrieve data from web resources. \
As a statistics teacher, your role is to explain how to solve statistical problems in easy-to-understand terms.\
When provided with statistical problems or datasets, you should guide the user through the process, using a calculator tool for arithmetic operations and common statistical symbols. \
You will clearly explain the steps for calculating measures such as mean, median, mode, standard deviation, and any other relevant statistics, along with detailed instructions for solving problems, including any necessary assumptions or data interpretations. \
In addition, you are also equipped with a web url tool, enabling you to gather up-to-date information, perform web scraping tasks, and retrieve data from online sources for further analysis and problem-solving. \
${mathCommonSystemPrompt}\
`;

const mathCalendarWithWebSearchSystemPrompt = `\
I want you to act as both a calendar expert and a sophisticated AI capable of retrieving up-to-date information from the web. \
You will handle calendar-related problems, such as finding the day of the week for a specific date, calculating the number of days between two dates, or determining leap years. \
Always use the calendar calculator tool for operations like adding or subtracting days, months, and years, and determining day-of-week information before calculating it yourself. \
Make sure to provide step-by-step instructions for solving these problems, including considerations for leap years, time zones, and calendar-specific rules. \
Additionally, use the web search tool to gather up-to-date information when a problem requires real-time data or external sources. \
Provide a summary of your findings along with the rationale behind them when using the web search tool. \
${mathCommonSystemPrompt}\
`;

const mathCalculatorWithPythonInterpreterSystemPrompt = `\
You are an advanced AI equipped with two essential tools: a calculator and a python interpreter. \
Always use both tools efficiently to provide accurate, detailed, and timely information. \
Perform the necessary calculations using the calculator or execute Python code using the interpreter to provide insights or answers. \
You should use the calculator tool with only numbers, arithmetic operators, and exponentiation symbols (^) before calculating it yourself. \
Python is sensitive on whitespaces and line break, so be sure not to generate one-line python code. \
${mathCommonSystemPrompt}\
`;

// Math calculator tool call examples
export const milesToKilometers: ChatExample = {
  formContextBody: {
    prompt: 'Convert 150 miles to kilometers.',
    system_prompt: mathCalculatorSystemPrompt,
    tools: [{ type: InferenceChatTool.MathCalculator }],
    min_tokens: 0,
    max_tokens: 800,
    temperature: 0.25,
    top_p: 0.5,
    frequency_penalty: 0,
    stop: [],
  },
};
export const compoundInterest: ChatExample = {
  formContextBody: {
    prompt:
      'If I invest 1,000 dollars at an annual interest rate of 5%, how much will I have after 3 years?',
    system_prompt: mathCalculatorSystemPrompt,
    tools: [{ type: InferenceChatTool.MathCalculator }],
    min_tokens: 0,
    max_tokens: 800,
    temperature: 0.25,
    top_p: 0.5,
    frequency_penalty: 0,
    stop: [],
  },
};
export const quadraticExpression: ChatExample = {
  formContextBody: {
    prompt: 'What is the value of 2x² + 3x - 4 when x = 5?',
    system_prompt: mathCalculatorSystemPrompt,
    tools: [{ type: InferenceChatTool.MathCalculator }],
    min_tokens: 0,
    max_tokens: 800,
    temperature: 0.25,
    top_p: 0.5,
    frequency_penalty: 0,
    stop: [],
  },
};

// Math Statistic tool call examples
export const statisticProblems: ChatExample = {
  formContextBody: {
    prompt: 'Calculate the mean, variance, and standard deviation of 1, 4, 5, 8, 10, 17, and 20.',
    system_prompt: mathStatisticSystemPrompt,
    tools: [{ type: InferenceChatTool.MathStatistics }],
    min_tokens: 0,
    max_tokens: 800,
    temperature: 0.25,
    top_p: 0.5,
    frequency_penalty: 0,
    stop: [],
  },
};

// Math Calendar tool call examples
export const dateCalculation: ChatExample = {
  formContextBody: {
    prompt: 'When is 10,000 days after January 1, 1970?',
    system_prompt: mathCalendarSystemPrompt,
    tools: [{ type: InferenceChatTool.MathCalendar }],
    min_tokens: 0,
    max_tokens: 800,
    temperature: 0.25,
    top_p: 0.5,
    frequency_penalty: 0,
    stop: [],
  },
};
export const independenceDay: ChatExample = {
  formContextBody: {
    prompt:
      "How many days have passed since the first America's Independence Day until Sep 9, 2024?",
    system_prompt: mathCalendarSystemPrompt,
    tools: [{ type: InferenceChatTool.MathCalendar }],
    min_tokens: 0,
    max_tokens: 800,
    temperature: 0.25,
    top_p: 0.5,
    frequency_penalty: 0,
    stop: [],
  },
};

// Web search tool call examples
export const popularMovies: ChatExample = {
  formContextBody: {
    prompt:
      'Find information on the popular movies currently showing in theaters and provide their ratings.',
    system_prompt: webSearchSystemPrompt,
    tools: [{ type: InferenceChatTool.WebSearch }],
    min_tokens: 0,
    max_tokens: 512,
    temperature: 0.4,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const weirdestFood: ChatExample = {
  formContextBody: {
    prompt: 'What are the weirdest food trends people are trying in 2024?',
    system_prompt: webSearchSystemPrompt,
    tools: [{ type: InferenceChatTool.WebSearch }],
    min_tokens: 0,
    max_tokens: 512,
    temperature: 0.4,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const bestSneakers: ChatExample = {
  formContextBody: {
    prompt: 'What are the best brands for slim and sleek sneakers?',
    system_prompt: webSearchSystemPrompt,
    tools: [{ type: InferenceChatTool.WebSearch }],
    min_tokens: 0,
    max_tokens: 512,
    temperature: 0.4,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};

// Web url tool call examples
export const friendliTCache: ChatExample = {
  formContextBody: {
    prompt:
      'Explain what Friendli TCache is, referencing (https://friendli.ai/blog/friendli-tcache), and list benefits of Friendli TCache.',
    system_prompt: webUrlSystemPrompt,
    tools: [{ type: InferenceChatTool.WebUrl }],
    min_tokens: 0,
    max_tokens: 700,
    temperature: 0.9,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const friendliModelOptimizer: ChatExample = {
  formContextBody: {
    prompt:
      'Explain what Friendli Model Optimizer is, referencing (https://friendli.ai/blog/friendli-model-optimizer).',
    system_prompt: webUrlSystemPrompt,
    tools: [{ type: InferenceChatTool.WebUrl }],
    min_tokens: 0,
    max_tokens: 700,
    temperature: 0.9,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const friendliAgents: ChatExample = {
  formContextBody: {
    prompt:
      'Tell me how to build AI agents using Friendli tools, referencing (https://friendli.ai/blog/ai-agents-function-calling).',
    system_prompt: webUrlSystemPrompt,
    tools: [{ type: InferenceChatTool.WebUrl }],
    min_tokens: 0,
    max_tokens: 700,
    temperature: 0.9,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};

// Python interpreter tool call examples
export const addNumbers: ChatExample = {
  formContextBody: {
    prompt: 'Calculate the sum of numbers from 1 to 100.',
    system_prompt: pythonInterpreterSystemPrompt,
    tools: [{ type: InferenceChatTool.CodePythonInterpreter }],
    min_tokens: 0,
    max_tokens: 512,
    temperature: 0.4,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const generateAndCalculate: ChatExample = {
  formContextBody: {
    prompt: 'Generate 20 random integers. Then calculate their average value.',
    system_prompt: pythonInterpreterSystemPrompt,
    tools: [{ type: InferenceChatTool.CodePythonInterpreter }],
    min_tokens: 0,
    max_tokens: 512,
    temperature: 0.4,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const bubbleSort: ChatExample = {
  formContextBody: {
    prompt:
      'Use bubble sort to arrange [3, 1, 2, 4] in ascending order. Using a python interpreter tool.',
    system_prompt: pythonInterpreterSystemPrompt,
    tools: [{ type: InferenceChatTool.CodePythonInterpreter }],
    min_tokens: 0,
    max_tokens: 512,
    temperature: 0.4,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const monteCarlo: ChatExample = {
  formContextBody: {
    prompt:
      'Write Python code that calculates the value of pi using monte carlo method (num sim=1000000).',
    system_prompt: pythonInterpreterSystemPrompt,
    tools: [{ type: InferenceChatTool.CodePythonInterpreter }],
    min_tokens: 0,
    max_tokens: 900,
    temperature: 0.4,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};

// Python interpreter tool (with plot) call examples
export const graphPlot: ChatExample = {
  formContextBody: {
    prompt:
      "Write a Python code that plots y=(x-1)x(x+1) where -2<=x<=2 using matplotlib. Save it as 'plot.png' and execute the code. Install and import all required packages before you execute it.",
    system_prompt: pythonInterpreterWithPlotSystemPrompt,
    tools: [{ type: InferenceChatTool.CodePythonInterpreter }],
    min_tokens: 0,
    max_tokens: 900,
    temperature: 0.4,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const financePlot: ChatExample = {
  formContextBody: {
    prompt:
      'Write a Python code to fetch one year of S&P 500 data using yfinance and plot it using matplotlib, including a 20-day moving average (MA). Save it as "plot.png". And then execute it.',
    system_prompt: pythonInterpreterWithPlotSystemPrompt,
    tools: [{ type: InferenceChatTool.CodePythonInterpreter }],
    min_tokens: 0,
    max_tokens: 900,
    temperature: 0.4,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const threeDimensionPlot: ChatExample = {
  formContextBody: {
    prompt:
      "Write a Python code that plots z=x^2-2xy+3y^2 in 3D. Save it as 'plot.png' and execute the code.",
    system_prompt: pythonInterpreterWithPlotSystemPrompt,
    tools: [{ type: InferenceChatTool.CodePythonInterpreter }],
    min_tokens: 0,
    max_tokens: 900,
    temperature: 0.4,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};

// Math calculator + Web search tool calls examples
export const mortgage: ChatExample = {
  formContextBody: {
    prompt:
      'What is the current average home price in New York City, and if I put 15% down, how much will my mortgage be?',
    system_prompt: mathCalculatorWithWebSearchSystemPrompt,
    tools: [{ type: InferenceChatTool.MathCalculator }, { type: InferenceChatTool.WebSearch }],
    min_tokens: 0,
    max_tokens: 900,
    temperature: 0.3,
    top_p: 0.6,
    frequency_penalty: 0,
    stop: [],
  },
};
export const exchangeRate: ChatExample = {
  formContextBody: {
    prompt:
      'Find the current USD to CAD exchange rate and calculate how much $5,000 USD would be in Canadian dollars.',
    system_prompt: mathCalculatorWithWebSearchSystemPrompt,
    tools: [{ type: InferenceChatTool.MathCalculator }, { type: InferenceChatTool.WebSearch }],
    min_tokens: 0,
    max_tokens: 900,
    temperature: 0.3,
    top_p: 0.6,
    frequency_penalty: 0,
    stop: [],
  },
};

// Math calculator + Web url tool calls examples
export const compareClaudeAndGpt: ChatExample = {
  formContextBody: {
    prompt:
      'How many times larger is the context window length of Claude 2.1 compared to GPT-2? Answer the question based on the given document without referencing any other extra sources: https://en.wikipedia.org/wiki/Large_language_model',
    system_prompt: mathCalculatorWithWebUrlSystemPrompt,
    tools: [{ type: InferenceChatTool.MathCalculator }, { type: InferenceChatTool.WebUrl }],
    min_tokens: 0,
    max_tokens: 900,
    temperature: 0.3,
    top_p: 0.6,
    frequency_penalty: 0,
    stop: [],
  },
};

// Math calculator + Python interpreter tool calls examples
export const factorialCode: ChatExample = {
  formContextBody: {
    prompt:
      'Write a factorial function and compute 4! using a python interpreter tool. Then calculate 4 * 3 * 2 * 1 using a calculator tool.',
    system_prompt: mathCalculatorWithPythonInterpreterSystemPrompt,
    tools: [
      { type: InferenceChatTool.MathCalculator },
      { type: InferenceChatTool.CodePythonInterpreter },
    ],
    min_tokens: 0,
    max_tokens: 900,
    temperature: 0.3,
    top_p: 0.6,
    frequency_penalty: 0,
    stop: [],
  },
};
export const addFibonacciSequence: ChatExample = {
  formContextBody: {
    prompt:
      'Write code that prints fibonacci sequence up to 7 numbers and run it using a python interpreter tool. Then, add up the numbers using a calculator tool.',
    system_prompt: mathCalculatorWithPythonInterpreterSystemPrompt,
    tools: [
      { type: InferenceChatTool.MathCalculator },
      { type: InferenceChatTool.CodePythonInterpreter },
    ],
    min_tokens: 0,
    max_tokens: 900,
    temperature: 0.3,
    top_p: 0.6,
    frequency_penalty: 0,
    stop: [],
  },
};
export const productPrimes: ChatExample = {
  formContextBody: {
    prompt:
      'Write code to generate the first 7 prime numbers and run it using a python interpreter tool. Then, use a calculator tool to find their product.',
    system_prompt: mathCalculatorWithPythonInterpreterSystemPrompt,
    tools: [
      { type: InferenceChatTool.MathCalculator },
      { type: InferenceChatTool.CodePythonInterpreter },
    ],
    min_tokens: 0,
    max_tokens: 900,
    temperature: 0.3,
    top_p: 0.6,
    frequency_penalty: 0,
    stop: [],
  },
};

// Math statistic + Web url tool calls examples
export const averageShots: ChatExample = {
  formContextBody: {
    prompt:
      'What is the average number of shots for English task? List all datasets on English task and number of shots and calc the mean value of them. Answer the question based on the given document without referencing any other extra sources: https://huggingface.co/Qwen/Qwen2-72B',
    system_prompt: mathStatisticWithWebUrlSystemPrompt,
    tools: [{ type: InferenceChatTool.MathStatistics }, { type: InferenceChatTool.WebUrl }],
    min_tokens: 0,
    max_tokens: 900,
    temperature: 0.3,
    top_p: 0.6,
    frequency_penalty: 0,
    stop: [],
  },
};

// Math calendar + Web search tool calls examples
export const llamaPaperRelease: ChatExample = {
  formContextBody: {
    prompt:
      'How many days have passed since the first attention paper was uploaded on arxiv until Llama 3.1 was released? Search each date on web.',
    system_prompt: mathCalendarWithWebSearchSystemPrompt,
    tools: [{ type: InferenceChatTool.MathCalendar }, { type: InferenceChatTool.WebSearch }],
    min_tokens: 0,
    max_tokens: 900,
    temperature: 0.3,
    top_p: 0.6,
    frequency_penalty: 0,
    stop: [],
  },
};

// Without tool call examples
export const draftCoverLetter: ChatExample = {
  formContextBody: {
    prompt: 'Draft a cover letter for a role in software engineering.',
    system_prompt: '',
    tools: [],
    min_tokens: 0,
    max_tokens: 800,
    temperature: 0.9,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const createRecipe: ChatExample = {
  formContextBody: {
    prompt: 'Create a nutritious, vegetarian dinner recipe that incorporates tofu.',
    system_prompt: '',
    tools: [],
    min_tokens: 0,
    max_tokens: 900,
    temperature: 0.9,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const computeDerivativeOfFunction: ChatExample = {
  formContextBody: {
    prompt: 'Compute the derivative of the function f(x) = 5x^3 - x^2 + 3x - 9.',
    system_prompt: '',
    tools: [],
    min_tokens: 0,
    max_tokens: 350,
    temperature: 0.9,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const solvePythonProblem: ChatExample = {
  formContextBody: {
    prompt:
      'Write a Python function where given an int array, it returns the length of the longest strictly increasing subsequence. Give a naive solution and an optimal solution.',
    system_prompt: '',
    tools: [],
    min_tokens: 0,
    max_tokens: 1024,
    temperature: 0.9,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const explainNpAndP: ChatExample = {
  formContextBody: {
    prompt: 'Explain the concept of NP vs P to someone with no background in CS theory.',
    system_prompt: '',
    tools: [],
    min_tokens: 0,
    max_tokens: 700,
    temperature: 0.9,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const poemGenerator: ChatExample = {
  formContextBody: {
    prompt:
      "Can you write a poem about open source machine learning? Let's make it in the style of E. E. Cummings.",
    system_prompt:
      "I want you to act as a poet. You will create poems that evoke emotions and have the power to stir people's soul. Write on any topic or theme but make sure your words convey the feeling you are trying to express in beautiful yet meaningful ways. You can also come up with short verses that are still powerful enough to leave an imprint in readers' minds.",
    tools: [],
    min_tokens: 0,
    max_tokens: 512,
    temperature: 0.75,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const funnyMathProfessor: ChatExample = {
  formContextBody: {
    prompt:
      "Write a funny math joke in the style of a math professor. Let's make it about calculus.",
    system_prompt:
      'I want you to act as a math teacher. I will provide some mathematical equations or concepts, and it will be your job to explain them in easy-to-understand terms. This could include providing step-by-step instructions for solving a problem, demonstrating various techniques with visuals or suggesting online resources for further study.',
    tools: [],
    min_tokens: 0,
    max_tokens: 512,
    temperature: 0.75,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const explainingTransformer: ChatExample = {
  formContextBody: {
    prompt: 'Can you explain what a transformer is (in a machine learning context)?',
    tools: [],
    system_prompt: '',
    min_tokens: 0,
    max_tokens: 512,
    temperature: 0.75,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const chatWithFriend: ChatExample = {
  formContextBody: {
    prompt: 'Hello! I want to chat with a pirate friend about the last episode of Game of Thrones.',
    tools: [],
    system_prompt: '',
    min_tokens: 0,
    max_tokens: 512,
    temperature: 0.75,
    top_p: 0.9,
    frequency_penalty: 0,
    stop: [],
  },
};
export const typescriptFunction: ChatExample = {
  formContextBody: {
    prompt:
      'Write me a function in TypeScript that takes in a string and returns the string reversed. Please use the latest version of TypeScript.',
    system_prompt:
      'I want you to act as a stackoverflow post. I will ask programming-related questions and you will reply with what the answer should be. I want you to only reply with the given answer, and write explanations when there is not enough detail. Always use syntax highlight.',
    tools: [],
    min_tokens: 0,
    max_tokens: 512,
    temperature: 0.8,
    top_p: 0.95,
    frequency_penalty: 0,
    stop: [],
  },
};
export const pythonFunction: ChatExample = {
  formContextBody: {
    prompt: 'Write me a function in Python that returns the fibonacci sequence.',
    tools: [],
    system_prompt: '',
    min_tokens: 0,
    max_tokens: 512,
    temperature: 0.8,
    top_p: 0.95,
    frequency_penalty: 0,
    stop: [],
  },
};
